import { toTimeStamp } from '@/util/util'
// import moment from 'moment'
import _ from 'underscore'
import log from 'loglevel'
import { accounting } from 'accounting'

// leads dashboards
import analyticsAPI from '@/api/analytics'

// single function to handle chart request payload
const handleChartPayload = (selected, state) => {
  // prepare request payload
  let payload = {}
  payload.key = selected.key
  payload.filters = { ...selected.filters }
  // send dealership value in array
  _.each(payload.filters, (filter, key) => {
    if (!_.isArray(filter)) {
      let tmp = []
      if (_.isObject(filter)) {
        if (filter.value !== -1) {
          tmp.push(filter.value)
        }
      } else {
        // check if it's not null or undefined
        if (filter) {
          tmp.push(filter)
        }
      }
      payload.filters[key] = tmp
    } else {
      payload.filters[key] = _.values(filter)
    }
  })

  // format times to timestamps
  payload.filters.date_time && payload.filters.date_time.length ? payload.filters.date_time = toTimeStamp(payload.filters.date_time) : payload.filters.date_time = []
  if (selected.date_time_comparison && selected.date_time_comparison.length) payload.filters.date_time_comparison = toTimeStamp(selected.date_time_comparison)
  // if comparison exist but is empty, send copy of date_time - setup on BI
  if (selected.date_time_comparison && !selected.date_time_comparison.length) payload.filters.date_time_comparison = payload.filters.date_time

  !selected.pagination ? payload.pagination = {} : payload.pagination = selected.pagination
  payload.pagination = _.omit(selected.pagination, 'totalItems')

  if (state.generic.dateTimeParam && state.generic.dateTimeParam !== '') {
    payload.filters[state.generic.dateTimeParam] = payload.filters.date_time
    delete payload.filters.date_time
  }

  if (state.generic.dateTimeComparisonParam && state.generic.dateTimeComparisonParam !== '') {
    payload.filters[state.generic.dateTimeComparisonParam] = payload.filters.date_time_comparison
    delete payload.filters.date_time_comparison
  }

  // set suffix '_modified' to chart key if mod-dashboard enabled
  if (state.generic.dateTimeParam && state.generic.dateTimeParam === 'date_time_modified' && selected.settings && selected.settings.keyModified) {
    payload.key = payload.key + '_modified'
  }
  // set suffix to date_time if modified
  if (selected.dateModified || selected.dateModified === '') {
    payload.filters.date_time_modified = payload.filters.date_time
    delete payload.filters.date_time
  }

  if (selected.exportDetailed) {
    payload.key = payload.key + '_detailed'
  }

  // add language to payload
  if (state.translations.currentLanguage && state.translations.currentLanguage !== 'undefined') {
    payload.language = state.translations.currentLanguage
  }

  return payload
}

const state = {
  exportData: [],
  dashboardCharts: []
}

const getDefaultState = () => {
  return {
    exportData: [],
    dashboardCharts: []
  }
}

// getters
const getters = {
  getCharts: state => state.dashboardCharts,
  getChartByKey: (state) => (key) => {
    // log.info('get chart by key', key, state)
    return state.dashboardCharts.find(chart => chart.key === key)
  }
}

// mutations
const mutations = {
  SET_CHARTS (state, charts) {
    state.dashboardCharts = charts
  },
  SET_CHART_DATA (state, payload) {
    let selectedChart = state.dashboardCharts.find(chart => chart.key === payload.key)
    selectedChart.data = payload.data
    selectedChart.pagination.totalItems = payload.data.metadata ? payload.data.metadata.total : 0
  },
  SET_CHART_LOADED (state, payload) {
    let selectedChart = state.dashboardCharts.find(chart => chart.key === payload.key)
    selectedChart.loaded = payload.value
  },
  SET_CHART_PAGINATION (state, payload) {
    let selectedChart = state.dashboardCharts.find(chart => chart.key === payload.key)
    selectedChart.pagination = payload.pagination
  },
  RESET_STATE (state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState())
    log.info('Dashboard charts state cleared')
  }
}

// actions
const actions = {
  fetchChartData ({ commit, getters, rootState }, selected) {
    commit('SET_CHART_LOADED', { key: selected.key, value: false })
    let preparedPayload = handleChartPayload(selected, rootState)

    // add query param withoutCount=true to chart requests
    let qp = ''
    if (selected.withoutCount) {
      qp = '&withoutCount=true'
    }

    return analyticsAPI.fetchData(preparedPayload, qp)
      .then(data => {
        // pivot data if needed, for now done on frontend
        if (selected.settings && selected.settings.pivotBy) {
          // let pivotBy = ['Open', 'OpenContract', 'Pending']
          let pivotBy = selected.settings.pivotBy
          let tmpOutput = []
          _.each(data.result, (item, key) => {
            if (!_.isEmpty(pivotBy)) {
              _.each(pivotBy, p => {
                let tmp = _.find(tmpOutput, r => {
                  return r.name === p
                })
                if (!tmp) {
                  tmp = {
                    name: p
                  }
                  tmpOutput.push(tmp)
                  tmp = _.find(tmpOutput, r => {
                    return r.name === p
                  })
                }
                tmp[item.name] = item[p] ? item[p] : null
              })
            }
          })
          data.result = tmpOutput
        }
        // convert number from KPI widget
        if (selected.numberFormatting && selected.numberFormatting.formatNr && selected.numberFormatting.formatNr !== '') {
          if (selected.numberFormatting.formatNr === 'number') {
            let formatNumberParams = {}
            if (process.env.VUE_APP_FORMAT_NUMBER_PARAMS) {
              try {
                formatNumberParams = JSON.parse(process.env.VUE_APP_FORMAT_NUMBER_PARAMS)
                if (selected.numberFormatting.decimal) formatNumberParams.precision = 2 // override decimal
                data.result[0].value = accounting.format(data.result[0].value, formatNumberParams)
              } catch (e) {}
            }
          }
          if (selected.numberFormatting.formatNr === 'price') {
            let formatPriceParams = {}
            if (process.env.VUE_APP_FORMAT_PRICE_PARAMS) {
              try {
                formatPriceParams = JSON.parse(process.env.VUE_APP_FORMAT_PRICE_PARAMS)
                if (selected.numberFormatting.decimal) formatPriceParams.precision = 2 // override decimal
                data.result[0].value = accounting.formatMoney(data.result[0].value, formatPriceParams)
              } catch (e) {}
            }
          }
        }
        if (data.errorCode) {
          commit('SET_CHART_LOADED', { key: selected.key, value: true })
          commit('SET_CHART_DATA', { key: selected.key, data: {} })
          return Promise.reject(data)
        } else {
          commit('SET_CHART_LOADED', { key: selected.key, value: true })
          commit('SET_CHART_DATA', { key: selected.key, data: data })
          return Promise.resolve(data)
        }
      })
  },
  fetchChartDataTotal ({ commit, rootState }, selected) {
    let preparedPayload = handleChartPayload(selected, rootState)
    return analyticsAPI.fetchData(preparedPayload, '')
  },
  exportTableData ({ commit, getters, rootState }, selected) {
    let preparedPayload = handleChartPayload(selected, rootState)
    preparedPayload.pagination.rowsPerPage = selected.pagination.totalItems // modify payload for full data export
    if (preparedPayload.key.includes('_detailed')) delete preparedPayload.pagination // omit pagination for export detailed
    return analyticsAPI.exportData(preparedPayload, selected.format)
      .then(data => {
        if (data.errorCode) {
          return Promise.reject(data)
        } else {
          return Promise.resolve(data)
        }
      })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
